'use client';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { ChevronUp } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

import ThemeToggle from './theme-toggle';

interface NavigationProps {
  scrollThreshold?: number;
}

const SkipLink = () => (
  <a
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-primary focus:text-white focus:rounded-md focus:font-medium focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:ring-offset-2"
    href="#main-content"
  >
    Skip to main content
  </a>
);

export function Navigation({ scrollThreshold = 400 }: NavigationProps) {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const { trackEvent } = useInteractionTracker();

  // Check scroll position relative to letter navigation section
  useEffect(() => {
    const toggleVisibility = () => {
      const letterNavigation = document.getElementById('letter-navigation');

      if (letterNavigation) {
        // Check if letter navigation has been completely scrolled past (above viewport)
        const rect = letterNavigation.getBoundingClientRect();
        const letterNavPassed = rect.bottom < 0;
        setShowScrollButton(letterNavPassed);
      } else {
        // Fallback: use simple scroll threshold if letter navigation not found
        setShowScrollButton(window.scrollY > scrollThreshold);
      }
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    toggleVisibility(); // Check initial state

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, [scrollThreshold]);

  const scrollToTop = useCallback(() => {
    // Track scroll-to-top
    void trackEvent({ action: 'click-scroll-to-top', category: 'nav', label: 'scroll-to-top' });

    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  }, [trackEvent]);

  return (
    <>
      <SkipLink />
      <nav
        aria-label="Main navigation"
        className="sticky top-0 z-50 transition-all duration-500"
      >
        <div className="w-full glass-nav z-10 relative">
          <div className="flex items-center transition-all duration-300 gap-4 justify-between py-2 max-w-screen-2xl mx-auto px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <Link
                aria-label="Go to Varsity Tutors homepage"
                href="https://www.varsitytutors.com"
                onClick={() =>
                  void trackEvent({ action: 'click-logo', category: 'nav', label: 'varsity-tutors' })
                }
                rel="noopener noreferrer"
                target="_blank"
              >
                <div className="flex items-center justify-center p-2 bg-gray-900 dark:bg-transparent rounded-lg">
                  <Image
                    alt="Varsity Tutors Logo"
                    className="h-7 w-auto"
                    height={28}
                    src="/hotmath/vt-logo-white.svg"
                    width={105}
                  />
                </div>
              </Link>

              <Link
                aria-label="Go to Hotmath homepage"
                className="hover:scale-[1.02] transition-transform duration-200"
                href="/"
                onClick={() => void trackEvent({ action: 'click-logo', category: 'nav', label: 'hotmath' })}
              >
                <span className="logo-text text-2xl font-bold tracking-tight max-w-[140px] overflow-hidden whitespace-nowrap text-gray-900 dark:text-white">
                  HotMath
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-2">
              {showScrollButton && (
                <div className="scroll-button-wrapper">
                  <button
                    aria-label="Scroll to top of page"
                    className="
                    group flex items-center justify-center
                    relative overflow-hidden
                    w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] rounded-lg
                    glass-subtle
                    hover:bg-surface-hover
                    hover:border-brand-secondary
                    hover:scale-105 hover:shadow-lg hover:shadow-brand-secondary/10
                    active:scale-95
                    transition-all duration-200 ease-out
                    focus-ring-primary
                    focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  "
                    onClick={scrollToTop}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToTop();
                      }
                    }}
                    type="button"
                  >
                    <ChevronUp
                      aria-hidden="true"
                      className="text-text-primary opacity-70 group-hover:opacity-100 transition-opacity duration-200"
                      size={16}
                    />
                  </button>
                </div>
              )}

              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navigation;
